// Tags, by name. `POST /api/tickets` takes names and creates any it does not know
// (docs/API.md §3.3), so this never needs an id and never needs a round trip to
// add a new one.
//
// Names are normalised to trimmed lowercase before they leave here: "Hardware"
// and "hardware" typed on two different calls would otherwise become two tags,
// and the queue filters and reports would silently split in half.
//
// The chips take the same neutral tokens as ChipSelect: a tag is not a status, so
// it stays off the `badge-*` hues and uses `.badge` for its shape alone.

import { useMemo, useState } from 'react';

import useListNavigation from './useListNavigation.js';
import ResultList from './ResultList.jsx';

function normalise(name) {
  return name.trim().toLowerCase();
}

export default function TagInput({ value, onChange, suggestions }) {
  const [draft, setDraft] = useState('');

  const matches = useMemo(() => {
    const needle = normalise(draft);
    if (!needle) return [];
    return (suggestions ?? [])
      .filter((tag) => tag.name.includes(needle) && !value.includes(tag.name))
      .slice(0, 8);
  }, [draft, suggestions, value]);

  function add(name) {
    const clean = normalise(name);
    if (!clean) return;
    if (!value.includes(clean)) onChange([...value, clean]);
    setDraft('');
  }

  const listNav = useListNavigation(matches, {
    onSelect: (tag) => add(tag.name),
    onEscape: () => setDraft(''),
  });

  function handleKeyDown(event) {
    if (event.key === 'Enter') {
      // With a suggestion highlighted the list handles it; otherwise take what was
      // typed, so a brand-new tag costs no extra keystroke.
      if (matches.length) {
        listNav.handleKeyDown(event);
        return;
      }
      event.preventDefault();
      add(draft);
      return;
    }
    if (event.key === 'Backspace' && draft === '' && value.length) {
      event.preventDefault();
      onChange(value.slice(0, -1));
      return;
    }
    listNav.handleKeyDown(event);
  }

  return (
    <div>
      <label className="field-label" htmlFor="tag-input">Tags</label>
      {value.length > 0 && (
        <ul className="mb-1 flex flex-wrap gap-1">
          {value.map((name) => (
            <li key={name} className="badge bg-sunken text-ink-secondary ring-line-strong">
              {name}
              <button
                type="button"
                className="text-ink-muted hover:text-ink"
                aria-label={`Remove tag ${name}`}
                onClick={() => onChange(value.filter((entry) => entry !== name))}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
      <input
        id="tag-input"
        className="field-input"
        role="combobox"
        aria-expanded={matches.length > 0}
        aria-controls="tags-listbox"
        aria-autocomplete="list"
        aria-activedescendant={matches.length ? `tags-option-${listNav.activeIndex}` : undefined}
        autoComplete="off"
        placeholder="Type and press Enter"
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={handleKeyDown}
      />
      {matches.length > 0 && (
        <ResultList
          idPrefix="tags"
          label="Existing tags"
          items={matches}
          activeIndex={listNav.activeIndex}
          onHover={listNav.setActiveIndex}
          onSelect={(tag) => add(tag.name)}
          getKey={(tag) => tag.id ?? tag.name}
          renderItem={(tag) => tag.name}
        />
      )}
    </div>
  );
}
